import React, { useEffect, useRef } from 'react';
import Styles from './TerminalPane.module.scss';
import { Terminal } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';
import { FitAddon } from '@xterm/addon-fit';

/**
 *
 * @param {object} props - The props passed to the component.
 * @param {function} props.setUserInput - A function to set user input for command parsing.
 * @param {function} props.resetActivityTimer - A function to reset the activity timer for the terminal session.
 * @returns {JSX.Element} - The rendered terminal pane.
 */
export function TerminalPane({ socket, setUserInput, resetActivityTimer }) {
	const terminalRef = useRef(null);
	const xtermRef = useRef(null);
	const fitAddonRef = useRef(null);

	const isInitialized = useRef(false);
	const currentInputRef = useRef('');
	const cursorPositionRef = useRef(0);

	const forbiddenKeys = [
		'\t', '\x1b[H', '\u001b[A', '\u001b[B',
		'\x1b[F', '\u0001', '\u0002', '\u0004', '\u0008',
		'\u0012', '\u0005', '\u0003', '\u0010', '\u000C', '\u000A',
		'\u001b[1;5D', '\u001b[1;5C', '\u001b[1;2D', '\u001b[1;2C',
		'\u001b[1;2A', '\u001b[1;2B', '\u001b[1;2H', '\u001b[1;2F',
		'\u001b[1;2P', '\u001b[1;2Q', '\u001b[1;2R', '\u001b[1;2S',
		'\x1b[15;2~'
	];

	const fitTerminal = () => {
		if (
			!isInitialized.current ||
			!terminalRef.current ||
			terminalRef.current.offsetParent === null ||
			terminalRef.current.offsetWidth === 0 ||
			terminalRef.current.offsetHeight === 0 ||
			!xtermRef.current ||
			!fitAddonRef.current
		) {
			return;
		}

		try {
			const currentDisplay = terminalRef.current.style.display;
			terminalRef.current.style.display = 'none';
			terminalRef.current.style.display = currentDisplay;

			requestAnimationFrame(() => {
				if (fitAddonRef.current && xtermRef.current) {
					fitAddonRef.current.fit();
					const { rows, cols } = xtermRef.current;
					socket.emit('terminal:resize', { rows, cols });
				}
			});
		} catch (error) {
			console.error('Error fitting terminal:', error);
		}
	};
	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		if (!terminalRef.current || !socket) return;

		xtermRef.current = new Terminal({
			cursorBlink: true,
			fontSize: 14,
			altClickMovesCursor: false,
			theme: {
				background: '#0C1F27',
			},
		});

		fitAddonRef.current = new FitAddon();
		xtermRef.current.loadAddon(fitAddonRef.current);
		try {
			if (terminalRef.current) {
				xtermRef.current.open(terminalRef.current);

				isInitialized.current = true;
			}
		} catch (error) {
			console.error('Error opening terminal:', error);
			return;
		}
		const xtermViewport = terminalRef.current.querySelector('.xterm-viewport');
		if (xtermViewport) {
			xtermViewport.style.overflow = 'hidden';
			xtermViewport.style.overflowY = 'hidden';
		}
		const fitTimeout = setTimeout(() => {
			fitTerminal();
		}, 500);

		const resizeObserver = new ResizeObserver(() => {
			fitTerminal();
		});
		if (terminalRef.current) {
			resizeObserver.observe(terminalRef.current);
		}

		xtermRef.current.onData((data) => {
			resetActivityTimer();
			if (data === '\r') {
				setUserInput(currentInputRef.current);
				currentInputRef.current = '';
				cursorPositionRef.current = 0;
			} else if (data === '\u001b[D') {
				// Left arrow key - move cursor left
				if (cursorPositionRef.current > 0) {
					cursorPositionRef.current--;
				}
			} else if (data === '\u001b[C') {
				// Right arrow key - move cursor right
				if (cursorPositionRef.current < currentInputRef.current.length) {
					cursorPositionRef.current++;
				}
			} else if (forbiddenKeys.includes(data)) {
				// Ignore forbidden characters
				return;
			} else if (data === '\u007F') {
				// Handling backspace
				if (cursorPositionRef.current > 0) {
					const input = currentInputRef.current;
					currentInputRef.current =
						input.slice(0, cursorPositionRef.current - 1) +
						input.slice(cursorPositionRef.current);
					cursorPositionRef.current--;
				}
			} else if (data === '\u001b[3~') {
				// Delete key - delete character at cursor position
				if (cursorPositionRef.current < currentInputRef.current.length) {
					const input = currentInputRef.current;
					currentInputRef.current =
						input.slice(0, cursorPositionRef.current) +
						input.slice(cursorPositionRef.current + 1);
				}
			} else {
				// Insert character at cursor position
				const input = currentInputRef.current;
				currentInputRef.current =
					input.slice(0, cursorPositionRef.current) +
					data +
					input.slice(cursorPositionRef.current);
				cursorPositionRef.current += data.length;
			}

			// Always emit input to socket
			socket.emit('terminal:input', data);
			return;
		});

		socket.on('terminal:output', (data) => {
			if (!xtermRef.current || !isInitialized.current) return;
			xtermRef.current.write(data);
		});

		return () => {
			clearTimeout(fitTimeout);
			resizeObserver.disconnect();
			if (xtermRef.current && isInitialized.current) {
				xtermRef.current.dispose();
				xtermRef.current = null;
			}
			if (socket.readyState === WebSocket.OPEN) {
				socket.disconnect();
			}
			isInitialized.current = false;
		}
	}, [socket]);

	return <div ref={terminalRef} className={Styles.terminal} id="terminalPane" />;
}
